import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { hasSubmittedFeedback } from "../../../services/feedbackSubmission";
import {
  cacheLastFormKey,
  isWithinCooldown,
  readFeedbackStore,
} from "./feedbackStore";
import { FeedbackFormDef } from "./forms";

/**
 * Should the feedback button be shown?
 *
 * Everyone may submit, but only signed-in users get a server-side check —
 * there is no identity to look an anonymous visitor up by.
 *
 * The guiding rule is asymmetric: **optimistically hide, never optimistically
 * show.** A cached key equal to the active form is written only after a
 * confirmed submit, so hiding on it immediately is safe and skips a query. A
 * missing or different key is NOT evidence the user hasn't submitted — they may
 * have used another device, or cleared storage — so showing on that basis makes
 * the button appear and then vanish once the query answers. Waiting ~200ms for
 * a corner button to fade in is imperceptible; a flicker is not.
 */
export const useFeedbackButtonVisible = (form: FeedbackFormDef) => {
  const { session, authResolved } = useAuth();
  const userId = session?.user?.id ?? null;

  // null = undetermined; render nothing rather than guessing.
  const [visible, setVisible] = useState<boolean | null>(null);

  useEffect(() => {
    // Nothing is known until the initial getSession() settles: until then a
    // signed-in user is indistinguishable from an anonymous one, and reading
    // `userId` would take the "show" branch below on their behalf. That is the
    // flicker this hook exists to avoid — and worse, a click landing in that
    // window posts user_id: null while the request still carries their JWT, so
    // `WITH CHECK (auth.uid() = user_id)` compares against NULL and the insert
    // is denied.
    if (!authResolved) {
      return;
    }

    // Submitted from this browser within the cooldown window. For anonymous
    // visitors this is the only signal there is — a courtesy, not enforcement.
    // Bounded rather than permanent so a shared browser is not silenced for
    // good; signed-in users are still suppressed permanently by the server
    // check below.
    if (isWithinCooldown(readFeedbackStore(userId), form.key)) {
      setVisible(false);
      return;
    }

    // Anonymous and no local record → show. Nothing to ask the server.
    if (!userId) {
      setVisible(true);
      return;
    }

    // Back to undetermined while the lookup runs. Without this, a sign-in that
    // happens with the button already up (shown on the anonymous branch) would
    // leave it up and then yank it away if the account has already submitted.
    setVisible(null);

    let cancelled = false;
    void hasSubmittedFeedback(form, userId).then((submitted) => {
      if (cancelled) {
        return;
      }
      if (submitted === null) {
        // Lookup failed. Stay hidden and retry on the next load: a false hide
        // costs one impression, whereas showing on error manufactures a
        // duplicate submission attempt.
        setVisible(false);
        return;
      }
      if (submitted) {
        // Repair a cold or cross-device cache.
        cacheLastFormKey(userId, form.key);
      }
      setVisible(!submitted);
    });

    return () => {
      cancelled = true;
    };
  }, [authResolved, userId, form]);

  const markSubmitted = useCallback(() => {
    cacheLastFormKey(userId, form.key);
    setVisible(false);
  }, [userId, form.key]);

  return { visible: visible === true, markSubmitted };
};
